/**
 * ClientPortalLayout — Reduced-lens layout for client portal (Phase 5.1)
 *
 * A simplified layout for clients that only shows Intake-stage tabs:
 * - Overview/Status
 * - Documents (Upload)
 * - Clarifications
 *
 * Scoped to the authenticated client's OWN case via magic-link JWT claim.
 * Does NOT show attorney/admin tabs, navigation, or other cases.
 *
 * Design: Black+orange only, CSS variables from index.css, DM Serif/Outfit/DM Mono.
 *
 * @see docs/plans/2026-06-26-redesign-build-blueprint.md §3.5
 * @see docs/plans/2026-06-25-redesign-design-spec.md
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import {
  Upload,
  ListChecks,
  LayoutDashboard,
  Shield,
  Clock,
  Activity,
  BookOpen,
  LogOut,
} from 'lucide-react';
import { BRAND_NAME } from '@/config/brand';
import { api } from '@/utils/api';
import { useClientSessionToken } from '@/hooks/useClientSessionToken';
import { useClerk } from '@/contexts/TestAuthContext';
import ClientCasePicker from '@/components/client/ClientCasePicker';
import { ClientPortalContext, portalDisplayName } from './ClientPortalContext';

const PORTAL_FETCH_TIMEOUT_MS = 20000;

// Intake-stage tabs for client portal
type ClientTab = 'overview' | 'upload' | 'clarify' | 'status' | 'prep-guide';

interface ClientPortalLayoutProps {
  children: React.ReactNode;
  activeTab: ClientTab;
  caseId?: string;
  clientName?: string;
  caseStatus?: string;
}

interface PortalData {
  report_id: string;
  claimant_name: string;
  client_name?: string;
  status: string;
  has_documents: boolean;
  has_clarifications: boolean;
  progress_percent?: number | null;
}

export default function ClientPortalLayout({
  children,
  activeTab,
  caseId: propCaseId,
  clientName: propClientName,
  caseStatus: propCaseStatus,
}: ClientPortalLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { token: pathToken } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const linkToken = pathToken ?? searchParams.get('token') ?? undefined;
  const session = useClientSessionToken({ enabled: !linkToken });
  const { signOut } = useClerk();

  const [pickedToken, setPickedToken] = useState<string | undefined>(undefined);
  const [pickedClarificationToken, setPickedClarificationToken] = useState<string | undefined>(undefined);
  const [pickerError, setPickerError] = useState<string | null>(null);
  const [pickerPending, setPickerPending] = useState<string | null>(null);
  const lastReportIdRef = useRef<string | null>(null);
  const portalRetryRef = useRef(false);

  const token = linkToken ?? pickedToken ?? (session.state === 'ready' ? session.token : undefined);
  const source: 'link' | 'session' | undefined = linkToken
    ? 'link'
    : (pickedToken || session.state === 'ready' ? 'session' : undefined);
  const clarificationToken = linkToken
    ? undefined
    : (pickedClarificationToken ?? (session.state === 'ready' ? session.clarification_token : undefined));

  const [portalData, setPortalData] = useState<PortalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const mintForReportId = useCallback(async (reportId: string) => {
    const res = await api.post<{
      token: string;
      expires_at: string;
      clarification_token?: string;
    }>(
      `/api/v1/client/cases/${reportId}/portal-session`,
      undefined,
      { showErrorToast: false }
    );
    setPickedToken(res.token);
    if (res.clarification_token) {
      setPickedClarificationToken(res.clarification_token);
    }
    lastReportIdRef.current = reportId;
    return res;
  }, []);

  const handleCaseSelect = useCallback(async (reportId: string) => {
    setPickerError(null);
    setPickerPending(reportId);
    try {
      await mintForReportId(reportId);
    } catch {
      setPickerError('Failed to open case. Please try again.');
    } finally {
      setPickerPending(null);
    }
  }, [mintForReportId]);

  const fetchPortalData = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setLoading(true);
      setError(null);
      const result = await api.get(`/api/v1/portal/${token}`, {
        showErrorToast: false,
        timeoutMs: PORTAL_FETCH_TIMEOUT_MS,
      });
      setPortalData(result);
    } catch (err: any) {
      if (quiet) return; // a failed background refresh keeps the data already shown
      if (err.message?.includes('404') || err.message?.includes('410')) {
        const reportId = portalData?.report_id || (session.state === 'ready' ? (session as any).report_id : undefined) || lastReportIdRef.current;
        if (!portalRetryRef.current && source === 'session' && reportId) {
          portalRetryRef.current = true;
          mintForReportId(reportId).catch(() => {
            setError('Your session has expired. Please sign in again.');
          });
          return;
        }
        setError(source === 'session'
          ? 'Your session has expired. Please sign in again.'
          : 'This link is invalid or has expired.'
        );
      } else if (err.name === 'AbortError') {
        setError('The portal took too long to load. Please refresh the page.');
      } else {
        setError(err.message || 'Failed to load portal');
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [token, mintForReportId]);

  const refresh = useCallback(() => fetchPortalData(true), [fetchPortalData]);
  useEffect(() => {
    if (token) {
      fetchPortalData();
    }
  }, [fetchPortalData, token]);

  const contextValue = useMemo(() => ({
    data: portalData,
    refresh,
    token,
    source,
    clarificationToken,
  }), [portalData, refresh, token, source, clarificationToken]);

  const handleTabChange = useCallback((tab: ClientTab) => {
    const routes: Record<ClientTab, string> = {
      overview: '/client',
      upload: '/client/upload',
      clarify: '/client/clarify',
      status: '/client/status',
      'prep-guide': '/client/prep-guide',
    };

    const targetUrl = source === 'link' && token
      ? `${routes[tab]}?token=${token}`
      : routes[tab];

    navigate(targetUrl);
  }, [navigate, token, source]);

  const effectiveCaseId = propCaseId || portalData?.report_id;
  const effectiveClientName = propClientName || portalDisplayName(portalData);
  // Unknown progress (e.g. document-scope token) hides the bar rather than showing a false 0%.
  const progressPercent = portalData?.progress_percent ?? null;

  if (!linkToken && session.state === 'loading') {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[var(--color-brand)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--color-text-secondary)]">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (!linkToken && session.state === 'no_case') {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-[var(--color-brand)] text-2xl">!</span>
          </div>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)] mb-2">No Cases Found</h1>
          <p className="text-[var(--color-text-secondary)] mb-6">
            No cases are linked to your account — contact your attorney.
          </p>
        </div>
      </div>
    );
  }

  if (token && loading && !portalData) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-[var(--color-brand)] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--color-text-secondary)]">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (error && !portalData) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-xl p-8 text-center">
          <div className="w-16 h-16 bg-[var(--color-bg-elevated)] border border-[var(--color-border)] rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-[var(--color-brand)] text-2xl">!</span>
          </div>
          <h1 className="text-xl font-bold text-[var(--color-text-primary)] mb-2">
            {source === 'session' ? 'Session Error' : 'Link Error'}
          </h1>
          <p className="text-[var(--color-text-secondary)] mb-6">{error}</p>
          <p className="text-sm text-[var(--color-text-muted)]">
            {source === 'session'
              ? 'Please sign out and sign back in, or contact your attorney.'
              : 'Please contact your representative for a new link.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)] flex flex-col">
      <header className="bg-[var(--color-bg-surface)] border-b border-[var(--color-border)] sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold" style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}>
                {/* BRAND_NAME already ends in "." — strip it so the orange accent
                    dot BECOMES the final period (not a second one → "Co.."). */}
                <span className="text-[var(--color-text-primary)]">{BRAND_NAME.replace(/\.$/, '')}</span>
                <span className="text-[var(--color-brand)]">.</span>
              </h1>
              <span className="text-[var(--color-text-dim)] text-sm hidden sm:inline">
                Client Portal
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-[var(--color-verified)]">
                <Shield className="w-4 h-4" />
                <span className="hidden sm:inline">Secure Connection</span>
              </div>
              {source === 'session' && (
                <button
                  onClick={() => {
                    signOut({ redirectUrl: '/sign-in' }).catch(() => {
                      window.location.assign('/sign-in');
                    });
                  }}
                  data-testid="client-sign-out"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign out</span>
                </button>
              )}
            </div>
          </div>

          {token && effectiveClientName && (
            <div className="mb-4">
              <p className="text-[var(--color-text-secondary)]">
                Hi <span className="text-[var(--color-text-primary)] font-medium">{effectiveClientName}</span>,
                please complete the items below
              </p>
            </div>
          )}

          {/* No token: nothing to measure or navigate — brand bar + the error only. */}
          {token && progressPercent != null && (
          <div className="mb-4">
            <div className="flex items-center justify-between text-sm mb-1">
              <span className="text-[var(--color-text-muted)] font-medium">Overall Progress</span>
              <span className="font-semibold text-[var(--color-brand)]">
                {progressPercent}% Complete
              </span>
            </div>
            <div className="h-2 bg-[var(--color-bg-elevated)] rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${
                  progressPercent === 100
                    ? 'bg-[var(--color-verified)]'
                    : 'bg-[var(--color-brand)]'
                }`}
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
          )}

          {token && (
          <nav className="flex flex-wrap gap-2" role="tablist" aria-label="Client portal tabs">
            <button
              onClick={() => handleTabChange('overview')}
              role="tab"
              aria-selected={activeTab === 'overview'}
              aria-controls="overview-panel"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'overview'
                  ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] border border-[var(--color-needs-human-tint-bd)]'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              Overview
            </button>

            <button
              onClick={() => handleTabChange('status')}
              role="tab"
              aria-selected={activeTab === 'status'}
              aria-controls="status-panel"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'status'
                  ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] border border-[var(--color-needs-human-tint-bd)]'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              <Activity className="w-4 h-4" />
              Status
            </button>

            <button
              onClick={() => handleTabChange('prep-guide')}
              role="tab"
              aria-selected={activeTab === 'prep-guide'}
              aria-controls="prep-guide-panel"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'prep-guide'
                  ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] border border-[var(--color-needs-human-tint-bd)]'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
              }`}
            >
              <BookOpen className="w-4 h-4" />
              Prep-Guide
            </button>

            {portalData?.has_documents !== false && (
              <button
                onClick={() => handleTabChange('upload')}
                role="tab"
                aria-selected={activeTab === 'upload'}
                aria-controls="upload-panel"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === 'upload'
                    ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] border border-[var(--color-needs-human-tint-bd)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                }`}
              >
                <Upload className="w-4 h-4" />
                Documents
                {portalData?.has_documents && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-[var(--color-brand)] text-[var(--color-bg-base)]">
                    Required
                  </span>
                )}
              </button>
            )}

            {portalData?.has_clarifications !== false && (
              <button
                onClick={() => handleTabChange('clarify')}
                role="tab"
                aria-selected={activeTab === 'clarify'}
                aria-controls="clarify-panel"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeTab === 'clarify'
                    ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] border border-[var(--color-needs-human-tint-bd)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]'
                }`}
              >
                <ListChecks className="w-4 h-4" />
                Clarifications
                {portalData?.has_clarifications && (
                  <span className="px-2 py-0.5 rounded-full text-xs bg-[var(--color-brand)] text-[var(--color-bg-base)]">
                    Required
                  </span>
                )}
              </button>
            )}
          </nav>
          )}
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-6">
        <ClientPortalContext.Provider value={contextValue}>
          {!linkToken && session.state === 'picker' && !pickedToken ? (
            <ClientCasePicker
              cases={session.cases}
              onSelect={handleCaseSelect}
              error={pickerError}
              pendingReportId={pickerPending}
            />
          ) : (
            children
          )}
        </ClientPortalContext.Provider>
      </main>

      <footer className="border-t border-[var(--color-border)] bg-[var(--color-bg-surface)] mt-auto">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-[var(--color-text-muted)]">
            <div className="flex items-center gap-4">
              {source === 'link' && (
                <>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>Link expires in 30 days</span>
                  </div>
                  <span className="hidden sm:inline text-[var(--color-border)]">|</span>
                </>
              )}
              <div className="flex items-center gap-1">
                <Shield className="w-4 h-4" />
                <span>Your data is secure</span>
              </div>
            </div>
            <p>© {new Date().getFullYear()} {BRAND_NAME}, Inc.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export type { ClientTab };
